import {DateTime, Interval} from "luxon";

import { useClassroomsStore } from "@/stores/Classrooms.store.ts";
import { useUsersStore } from "@/stores/Users.store.ts";

import { Classroom } from "@/models/Classroom.model.ts";
import { Organisation } from "@/models/Organisation.model.ts";
import { Valid } from "luxon/src/_util";

export class CopySelection {

	public interval: Interval<Valid>;
	public themeNames: string[];
	public classroomId: string;

	private classroom: Classroom | null = null;
	private organisation: Organisation | null = null;

	constructor(
		interval: Interval,
		themeNames: string[] = [],
		classroomId: string = ''
	) {
		if (!interval.isValid) {
			throw new Error('Invalid interval');
		}

		this.interval = interval;
		this.themeNames = themeNames;
		this.classroomId = classroomId;
	}

	public get isFromDifferentClassroom()
	{
		const currentClassroomId = useClassroomsStore().currentClassroom?.id;
		return this.classroomId && currentClassroomId && currentClassroomId !== this.classroomId;
	}

	public get fromClassroom(): Classroom | null
	{
		this.loadClassroomAndOrganisation();
		return this.classroom;
	}

	public get fromOrganisation(): Organisation | null
	{
		this.loadClassroomAndOrganisation();
		return this.organisation;
	}

	// Check if the current user has access to the classroom the copy selection is from
	public get isAvailable()
	{
		if (!this.fromClassroom) {
			return false;
		}
		return true;
	}

	/**
	 * Return the name of the classroom the copy selection is from.
	 * If the current user is not in the same organisation as the classroom,
	 * the organisation name is prepended to the classroom name.
	 */
	public get classroomName(): string
	{
		const organisationsStore = useUsersStore();

		return (organisationsStore.currentOrganisation?.id !== this.fromOrganisation.id ? this.fromOrganisation?.name + ' > ' : '') +
			this.fromClassroom?.name;
	}

	private loadClassroomAndOrganisation(): void
	{
		if (this.classroom && this.organisation) {
			return;
		}

		if (!this.classroomId) {
			return;
		}

		const user = useUsersStore().me;
		if (!user) {
			return;
		}

		for (let i = 0; i < user.roles.length; i++) {
			for (let j = 0; j < user.roles[i].organisation.classrooms.length; j ++) {
				if (this.classroomId === user.roles[i].organisation.classrooms[j].id) {
					this.classroom = user.roles[i].organisation.classrooms[j];
					this.organisation = user.roles[i].organisation;
					return;
				}
			}
		}

	}

	public serialize(): any {
		return {
			interval: {
				start: this.interval.start.toISO(),
				end: this.interval.end.toISO()
			},
			themeNames: this.themeNames,
			sourceClassroomId: this.classroomId,
		};
	}

	public static deserialize(data: any): CopySelection {
		return new CopySelection(
			Interval.fromDateTimes(
				DateTime.fromISO(data.interval.start),
				DateTime.fromISO(data.interval.end)
			),
			data.themeNames ?? [],
			data.sourceClassroomId ?? null
		);
	}
}
